import { useEffect, useRef, useState } from 'react';
import { bindMediaAudioFocus, previewVoice } from '@orborix/voice-client';
import { IconBulb, IconBulbFilled, IconRefresh, IconSparkleOutline, IconXSmall } from '../icons.js';
import type { InspireAngle, InspireSuggestion } from '../../../lib/suggestApi.js';

/** Label en español por ángulo de sugerencia (Fase 14C). */
const ANGLE_LABELS: Record<InspireAngle, string> = {
  direct: 'Respuesta directa',
  detail: 'Sumá un detalle',
  question: 'Devolvé una pregunta',
};

interface InspireButtonProps {
  onClick: () => void;
  active: boolean;
  disabled: boolean;
}

export function InspireButton({ onClick, active, disabled }: InspireButtonProps) {
  return (
    <button
      className={`inspire-btn inspire-btn-icon-only ${active ? 'is-active' : ''}`}
      onClick={onClick}
      disabled={disabled}
      aria-label="Inspirar respuesta"
      title="Inspirar respuesta"
    >
      {active ? <IconBulbFilled size={18} /> : <IconBulb size={18} />}
    </button>
  );
}

interface InspireCardProps {
  suggestions: ReadonlyArray<InspireSuggestion>;
  idx: number;
  loading: boolean;
  error: string | null;
  voiceId: string;
  onCycle: () => void;
  onUse: (sug: InspireSuggestion) => void;
  onRetry: () => void;
  onClose: () => void;
  /**
   * La Cabina desde A1 (Emmanuel, 22/10, card #603): cuenta sólo lo dicho en voz alta, así que
   * «Usar esto» no la manda: la deja a la vista para leerla (la tarjeta se cierra al hablar).
   */
  readAloud?: boolean;
}

export function InspireCard({
  suggestions,
  idx,
  loading,
  error,
  voiceId,
  onCycle,
  onUse,
  onRetry,
  onClose,
  readAloud = false,
}: InspireCardProps) {
  const [playing, setPlaying] = useState(false);
  /** La idea elegida para leer en voz alta (`readAloud`). */
  const [reading, setReading] = useState<InspireSuggestion | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  const sug = reading ?? suggestions[idx % suggestions.length];

  // Al desmontar soltamos el <audio> y revocamos la blob URL viva: desmontar
  // mientras suena —o un play() que rechaza antes de disparar onended/onerror—
  // dejaría la URL sin revocar y filtraría el blob.
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  const play = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (playing || !sug) return;
    setPlaying(true);
    try {
      // "Escuchar" usa el endpoint de preview del paquete con el voiceId activo.
      const blob = await previewVoice(voiceId, sug.en);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      const audio = new Audio(url);
      // Un solo audio a la vez en toda la app (ver `audioFocus`): la idea hablada
      // calla al coach en vivo igual que el replay de una burbuja.
      bindMediaAudioFocus(audio, 'speak:inspire');
      audioRef.current = audio;
      const release = (): void => {
        setPlaying(false);
        URL.revokeObjectURL(url);
        if (urlRef.current === url) urlRef.current = null;
      };
      audio.onended = release;
      audio.onerror = release;
      await audio.play();
    } catch {
      // Preview puede fallar si el backend no tiene la key — degradamos sin romper.
      setPlaying(false);
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    }
  };

  const tagLabel = reading
    ? 'LEELA EN VOZ ALTA'
    : loading
      ? 'PENSANDO…'
      : error
        ? 'IDEA'
        : `IDEA · ${idx + 1}/${suggestions.length}`;

  return (
    <div className="inspire-card slide-up">
      <div className="inspire-card-head">
        <div className="inspire-card-tag">
          <IconSparkleOutline size={12} />
          <span>{tagLabel}</span>
        </div>
        <button className="inspire-card-x" onClick={onClose} aria-label="Cerrar">
          <IconXSmall />
        </button>
      </div>

      {loading && !reading ? (
        <div className="inspire-card-body inspire-loading" aria-busy="true">
          <span className="inspire-skel inspire-skel-en" />
          <span className="inspire-skel inspire-skel-es" />
        </div>
      ) : error && !reading ? (
        <>
          <div className="inspire-card-body inspire-error">{error}</div>
          <div className="inspire-card-actions">
            <button
              className="inspire-action primary"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              <IconRefresh size={11} />
              <span>Reintentar</span>
            </button>
          </div>
        </>
      ) : sug ? (
        <>
          <div className="inspire-card-body">
            {sug.angle && <div className="inspire-angle">{ANGLE_LABELS[sug.angle]}</div>}
            <div className="inspire-en">{sug.en}</div>
            <div className="inspire-es">{sug.es}</div>
            {reading && (
              <div className="inspire-read-note">
                Decila con el micrófono: lo dicho en voz alta cuenta.
              </div>
            )}
          </div>

          <div className="inspire-card-actions">
            <button className="inspire-action ghost" onClick={(e) => void play(e)}>
              {playing ? (
                <div className="bars playing" style={{ height: 9, color: 'var(--lime)' }}>
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              ) : (
                <IconSparkleOutline size={10} />
              )}
              <span>{playing ? 'Reproduciendo…' : 'Escuchar'}</span>
            </button>
            {!reading && suggestions.length > 1 && (
              <button
                className="inspire-action ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onCycle();
                }}
              >
                <IconRefresh size={11} />
                <span>Otra</span>
              </button>
            )}
            {!reading && (
              <button
                className="inspire-action primary"
                onClick={(e) => {
                  e.stopPropagation();
                  if (readAloud) setReading(sug);
                  else onUse(sug);
                }}
              >
                <span>Usar esto</span>
              </button>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
